import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { closeModal, openModal } from 'interface/user/_redux/actions';
import { BasicModal, DefaultButton, PageContentPreloader } from 'common/components';
import { getRewardFrequencies } from 'backoffice/partnership/_redux';
import { getUserPartnerAccounts, getPartnershipConditionsList } from '../../_redux';
import UserPartnerAccountsTable from '../UserPartnerAccountsTable';
import UserPartnerAccountCreateForm from '../UserPartnerAccountCreateForm';

import { staticStyles } from './style';

const UserPartnerAccountsPanel = ({
  id,
  profile,
  getUserPartnerAccounts,
  getRewardFrequencies,
  userPartnerAccounts,
  userPartnerAccountsIsLoaded,
  rewardFrequencies,
  modals,
  openModal,
  closeModal,
  getPartnershipConditionsList,
  partnershipConditionsList,
  partnershipSystem,
  addons,
}) => {
  const accountCreatePermission = profile.perms.partnershipAccountCreate;
  const partnershipConditionsPermission = Boolean(
    profile.isAdmin || profile.perms.superUserPerm || profile.perms.partnershipConditions
  );
  const advancedPartnershipSystemEnabled = addons.includes('advancedPartnershipSystem');
  const canCreatePartnerAccount = accountCreatePermission;
  const simplePartnerSystem = partnershipSystem === 'simple';
  const bottomUpConditions = partnershipConditionsList.filter(condition => condition.distributionFlow !== 'top_down');

  useEffect(() => {
    getUserPartnerAccounts(id);
  }, [getUserPartnerAccounts, id]);

  useEffect(() => {
    if (advancedPartnershipSystemEnabled && partnershipConditionsPermission) {
      getPartnershipConditionsList(id);
    }
  }, [getPartnershipConditionsList, id, advancedPartnershipSystemEnabled, partnershipConditionsPermission]);

  useEffect(() => {
    if (advancedPartnershipSystemEnabled && partnershipConditionsPermission) {
      getRewardFrequencies();
    }
  }, [getRewardFrequencies, advancedPartnershipSystemEnabled, partnershipConditionsPermission]);

  const handleOpenModal = modalName => {
    getPartnershipConditionsList(id);
    openModal(modalName);
  };

  return (
    <PageContentPreloader ready={userPartnerAccountsIsLoaded}>
      <div className="UserPartnerAccountsPanel__button-inner">
        {canCreatePartnerAccount && !simplePartnerSystem && (
          <DefaultButton
            textId="createPlatformAccount"
            onClick={() => handleOpenModal('createPartnerAccount')}
            filled
            plus
          />
        )}
      </div>

      <UserPartnerAccountsTable
        data={userPartnerAccounts}
        partnershipConditionsPermission={partnershipConditionsPermission}
        partnershipConditionsList={partnershipConditionsList}
        userId={id}
        getUserPartnerAccounts={getUserPartnerAccounts}
        simplePartnerSystem={simplePartnerSystem}
        rewardFrequencies={rewardFrequencies}
      />

      {canCreatePartnerAccount && (
        <BasicModal
          isOpen={modals.createPartnerAccount}
          captionId="createPartnerAccount"
          onRequestClose={() => closeModal('createPartnerAccount')}>
          <UserPartnerAccountCreateForm
            userId={+id}
            rewardFrequencies={rewardFrequencies}
            partnershipConditionsList={bottomUpConditions}
            onSubmitSuccess={() => {
              closeModal('createPartnerAccount');
              getUserPartnerAccounts(id);
            }}
          />
        </BasicModal>
      )}

      <style jsx>{staticStyles}</style>
    </PageContentPreloader>
  );
};

UserPartnerAccountsPanel.propTypes = {
  profile: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  getUserPartnerAccounts: PropTypes.func.isRequired,
  getRewardFrequencies: PropTypes.func.isRequired,
  userPartnerAccountsIsLoaded: PropTypes.bool.isRequired,
  userPartnerAccounts: PropTypes.array.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  modals: PropTypes.object.isRequired,
  getPartnershipConditionsList: PropTypes.func.isRequired,
  partnershipConditionsList: PropTypes.array.isRequired,
  rewardFrequencies: PropTypes.array.isRequired,
  addons: PropTypes.array.isRequired,
  partnershipSystem: PropTypes.string.isRequired,
};

export default connect(
  state => ({
    profile: state.user.profile,
    userPartnerAccounts: state.backoffice.users.userPartnerAccounts,
    userPartnerAccountsIsLoaded: state.backoffice.users.userPartnerAccountsIsLoaded,
    modals: state.user.modals,
    partnershipConditionsList: state.backoffice.users.partnershipConditionsList,
    partnershipSystem: state.interfaceConfig.partnership.partnershipSystem,
    rewardFrequencies: state.backoffice.partnership.rewardFrequencies,
    addons: state.interfaceConfig.addons,
  }),
  {
    getUserPartnerAccounts: userId => getUserPartnerAccounts.request(userId),
    openModal: name => openModal(name),
    closeModal: name => closeModal(name),
    getPartnershipConditionsList: userId => getPartnershipConditionsList.request(userId),
    getRewardFrequencies: () => getRewardFrequencies.request(),
  }
)(UserPartnerAccountsPanel);
export { UserPartnerAccountsPanel };
